import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHero } from '@/components/shared/PageHero';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { SERVICES, DOWNLOADABLE_FORMS } from '@/data/mockData';
import {
  FileCheck2, Home, HandHeart, Briefcase, Store, ScrollText, Download,
  ArrowRight, FileText, Clock, ChevronRight,
} from 'lucide-react';

const ICONS = { FileCheck2, Home, HandHeart, Briefcase, Store, ScrollText };

const EServices = () => {
  const groups = SERVICES.reduce((acc, s) => {
    acc[s.group] = acc[s.group] || [];
    acc[s.group].push(s);
    return acc;
  }, {});

  return (
    <div data-testid="eservices-page">
      <PageHero
        eyebrow="E-Services"
        title="Online Services Hub"
        subtitle="Browse the full catalog of barangay services. Submit requests online — pick up at the barangay hall."
        breadcrumbs={[{ label: 'E-Services' }]}
      >
        <div className="flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="eservices-request-cta">
            <Link to="/request-document"><FileText className="mr-2 h-4 w-4" /> Request a document</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="bg-white/5 border-white/30 text-primary-foreground hover:bg-white/15 hover:text-primary-foreground" data-testid="eservices-track-cta">
            <Link to="/track-request">Track a request</Link>
          </Button>
        </div>
      </PageHero>

      <section className="container-app section-padding">
        {Object.entries(groups).map(([group, items]) => (
          <div key={group} className="mb-12 last:mb-0">
            <SectionHeader eyebrow="Services" title={group} className="mb-6" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((s) => {
                const Icon = ICONS[s.icon] || FileCheck2;
                return (
                  <Card key={s.id} className="p-6 card-elevated border-0" data-testid={`service-${s.slug}`}>
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground shadow-md-soft">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-heading font-semibold text-lg">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{s.purpose}</p>
                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-lg bg-secondary/60 p-2">
                        <div className="text-muted-foreground">Fee</div>
                        <div className="font-semibold">{s.fee}</div>
                      </div>
                      <div className="rounded-lg bg-secondary/60 p-2">
                        <div className="text-muted-foreground">Processing</div>
                        <div className="font-semibold inline-flex items-center gap-1"><Clock className="h-3 w-3" />{s.processing}</div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-foreground/80 mb-1.5">Requirements</div>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        {s.requirements.map((r, i) => (
                          <li key={i} className="flex items-start gap-1.5"><ChevronRight className="h-3 w-3 mt-0.5 text-primary flex-shrink-0" />{r}</li>
                        ))}
                      </ul>
                    </div>
                    <Button asChild className="w-full mt-5 bg-gradient-primary text-primary-foreground" data-testid={`service-request-${s.slug}`}>
                      <Link to={`/request-document?type=${encodeURIComponent(s.title)}`}>
                        Request now <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {/* DOWNLOADABLE FORMS */}
        <SectionHeader
          eyebrow="Forms"
          title="Downloadable Forms"
          description="Prefer to fill out paper forms? Download, print, and bring them to the barangay hall."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {DOWNLOADABLE_FORMS.map((f) => (
            <Card key={f.id} className="p-4 border-0 card-elevated flex items-center gap-4">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent-soft text-accent flex-shrink-0">
                <FileText className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{f.title}</div>
                <div className="text-xs text-muted-foreground">{f.type} · {f.size} · Updated {f.updated}</div>
              </div>
              <Button size="icon" variant="ghost" data-testid={`download-${f.id}`}>
                <Download className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

export default EServices;
